import {
  Autocomplete,
  Button,
  Group,
  Stack,
  Text,
  type ComboboxItem,
  type ComboboxParsedItem,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useCreateProduct,
  useCreateVersion,
  useProducts,
  useVersions,
} from "../../hooks/useMaintenance.js";
import { filterSoftwareOptions, normalizeLabel } from "./softwareOptionsFilter.js";

interface Props {
  onPick: (picked: { productId: string; versionId: string }) => void;
  /**
   * Vorbelegung für „Bearbeiten" eines bestehenden Softwarestands (Befund B2)
   * — startet mit bereits gewähltem Produkt/Version statt leer.
   */
  initial?: { productId: string; productName: string; versionId: string; versionLabel: string };
  /**
   * Beschriftung des Übernehmen-Knopfs — je nach Einsatzort bedeutet
   * „Übernehmen" etwas anderes (Zielstand einem Wartungsvorgang hinzufügen
   * vs. Version für einen Softwarestand wählen). Befund U4: der Name muss die
   * Wirkung nennen, statt in beiden Kontexten identisch und unklar zu sein.
   */
  pickLabel: string;
}

/**
 * Unterscheidet einzelne Einträge von Gruppen in Mantines geparsten
 * Combobox-Daten — hier gibt es nie Gruppen (siehe `data`-Props unten),
 * dient nur der Typsicherheit für die eigene Filterfunktion.
 */
function isSelectableItem(item: ComboboxParsedItem): item is ComboboxItem {
  return "value" in item;
}

/**
 * Produkt und Version wählen — und beides beim Tippen anlegen, wenn es fehlt
 * (Spec E9). Die Vorschlagsliste zeigt Bestehendes zuerst und nennt die
 * Nutzung, damit man zugreift statt neu anzulegen.
 */
export function SoftwarePicker({ onPick, initial, pickLabel }: Props) {
  const { t } = useTranslation();
  const products = useProducts();
  const createProduct = useCreateProduct();
  const createVersion = useCreateVersion();

  const [productId, setProductId] = useState<string | null>(initial?.productId ?? null);
  const [productText, setProductText] = useState(initial?.productName ?? "");
  const [versionText, setVersionText] = useState(initial?.versionLabel ?? "");
  const [line, setLine] = useState<string | null>(null);

  const versions = useVersions(productId);

  const productOptions = (products.data?.items ?? []).map((p) => ({
    value: p.name,
    label: `${p.name} (${p.systemCount})`,
    id: p.id,
  }));
  const versionOptions = (versions.data ?? [])
    .slice()
    .sort((a, b) => b.systemCount - a.systemCount)
    .map((v) => ({ value: v.label, label: `${v.label} (${v.systemCount})`, id: v.id }));

  // Dieselbe Normalisierung für Suche, Vorhandensein-Prüfung UND Auswahl —
  // sonst hält `xExists` etwas für vorhanden, das die anschließende exakte
  // Suche nicht wiederfindet (Review-Befund: leerer Bildschirm bei
  // abweichender Schreibweise, z. B. nachlaufendes Leerzeichen).
  const matchedProduct = productOptions.find(
    (o) => normalizeLabel(o.value) === normalizeLabel(productText),
  );
  const productExists = matchedProduct !== undefined;
  const matchedVersion = versionOptions.find(
    (o) => normalizeLabel(o.value) === normalizeLabel(versionText),
  );
  const lineName = versions.data?.find((v) => v.id === line)?.label ?? null;

  return (
    <Stack gap="xs">
      <Autocomplete
        size="xs"
        label={t("maintenance.picker.product")}
        data={productOptions.map((o) => o.value)}
        filter={({ options, search }) =>
          filterSoftwareOptions(options.filter(isSelectableItem), search)
        }
        value={productText}
        onChange={(value) => {
          setProductText(value);
          setProductId(
            productOptions.find((o) => normalizeLabel(o.value) === normalizeLabel(value))?.id ??
              null,
          );
          setVersionText("");
          setLine(null);
        }}
      />
      {productText.trim().length > 0 && !productExists && (
        <>
          <Button
            size="xs"
            variant="light"
            loading={createProduct.isPending}
            onClick={() =>
              createProduct.mutate(
                { name: productText.trim() },
                { onSuccess: (created) => setProductId(created.id) },
              )
            }
          >
            {t("maintenance.picker.createProduct", { name: productText.trim() })}
          </Button>
          {createProduct.isError && (
            <Text c="red" size="xs">
              {t("maintenance.card.saveError")}
            </Text>
          )}
        </>
      )}

      <Autocomplete
        size="xs"
        label={t("maintenance.picker.version")}
        disabled={productId === null}
        data={versionOptions.map((o) => o.value)}
        filter={({ options, search }) =>
          filterSoftwareOptions(options.filter(isSelectableItem), search)
        }
        value={versionText}
        onChange={setVersionText}
      />
      {productId !== null && versionText.trim().length > 0 && (
        <Group gap="xs">
          {matchedVersion ? (
            <Button
              size="xs"
              onClick={() => onPick({ productId, versionId: matchedVersion.id })}
            >
              {pickLabel}
            </Button>
          ) : (
            <Button
              size="xs"
              variant="light"
              loading={createVersion.isPending}
              onClick={() =>
                createVersion.mutate(
                  { productId, label: versionText.trim() },
                  {
                    onSuccess: (created) => {
                      setLine(created.parentVersionId);
                      onPick({ productId, versionId: created.id });
                    },
                  },
                )
              }
            >
              {t("maintenance.picker.createVersion", { label: versionText.trim() })}
            </Button>
          )}
        </Group>
      )}
      {createVersion.isError && (
        <Text c="red" size="xs">
          {t("maintenance.card.saveError")}
        </Text>
      )}

      {lineName !== null && (
        <Text size="xs" c="dimmed">
          {t("maintenance.picker.lineAssigned", { name: lineName })}
        </Text>
      )}
    </Stack>
  );
}
